import React, { useState } from 'react';
import { Users, ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import type { Band } from '../../types/database';

interface BandSearchSelectProps {
  bands: Band[];
  selectedBand: Band | null;
  onSelectBand: (band: Band | null) => void;
  placeholder?: string;
  error?: string;
}

export function BandSearchSelect({ 
  bands, 
  selectedBand, 
  onSelectBand, 
  placeholder = 'Select band...',
  error 
}: BandSearchSelectProps) {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounce(searchQuery, 300);

  // Filter bands based on search query
  const filteredBands = bands.filter(band => 
    !debouncedSearch || 
    band.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    band.location?.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const handleBandSelect = (band: Band) => {
    onSelectBand(band);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    onSelectBand(null);
    setShowDropdown(false);
    setSearchQuery('');
  };

  return (
    <div 
      className="relative"
      onBlur={(e) => {
        if (!e.relatedTarget?.closest('.band-search-dropdown')) {
          setShowDropdown(false);
        }
      }}
    >
      <div className="w-full">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className={`w-full px-4 py-2 bg-dark border rounded-lg text-light flex items-center justify-between hover:border-gray-custom/40 transition-colors ${
            error ? 'border-red-500/40' : 'border-gray-custom/20'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-light/40" />
            <span className="truncate">
              {selectedBand ? selectedBand.name : placeholder}
            </span>
          </div>
          <ChevronDown className={`w-5 h-5 text-light/40 transition-transform ${showDropdown ? 'transform rotate-180' : ''}`} />
        </button>

        {showDropdown && (
          <div className="band-search-dropdown absolute z-[100] w-full mt-1 bg-[#232323] border border-gray-custom/20 rounded-lg shadow-lg">
            <div className="p-3 border-b border-gray-custom/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-light/40" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('bands.inviteMember.searchPlaceholder')}
                  className="w-full pl-9 pr-3 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:outline-none focus:border-gray-custom/40"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {selectedBand && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="w-full px-4 py-3 text-left hover:bg-gray-custom/10 text-light/60 border-b border-gray-custom/10"
                >
                  <span className="italic">{t('common.clearAll')}</span>
                </button>
              )}
              
              {filteredBands.map((band) => (
                <button
                  key={band.id}
                  type="button"
                  onClick={() => handleBandSelect(band)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-custom/10 text-light/80 flex items-center space-x-3 ${
                    selectedBand?.id === band.id ? 'bg-light/10' : ''
                  }`}
                >
                  <div className="w-10 h-10 bg-gray-custom/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {band.avatar_url ? (
                      <img
                        src={band.avatar_url}
                        alt={band.name}
                        className="w-full h-full object-cover"
                       loading="lazy" />
                    ) : (
                      <Users className="w-4 h-4 text-light/60" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{band.name}</div>
                    {band.location && (
                      <div className="text-sm text-light/60 truncate">{band.location}</div>
                    )}
                    {band.genres && band.genres.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {band.genres.slice(0, 2).map((genre) => (
                          <span
                            key={genre}
                            className="px-1.5 py-0.5 bg-tertiary rounded text-xs text-light"
                          >
                            {genre}
                          </span>
                        ))}
                        {band.genres.length > 2 && (
                          <span className="text-xs text-light/40">
                            +{band.genres.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))}
              
              {filteredBands.length === 0 && (
                <div className="px-4 py-6 text-light/40 text-center">
                  {debouncedSearch ? t('bands.inviteMember.noMusiciansFound') : 'No bands available'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}